import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();

  // Handle Google OAuth callback (hash fragment session_id)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      if (sid) {
        api.post("/auth/google/session", { session_id: sid })
          .then(r => { setUser(r.data); window.location.hash = ""; nav("/dashboard"); })
          .catch(e => toast.error(formatApiError(e.response?.data?.detail)));
      }
    }
  }, [setUser, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Benvenuto!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    const redirect = `${window.location.origin}/login`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto py-16 px-6">
        <h1 className="text-3xl font-black tracking-tighter mb-8">{t("login_title")}</h1>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="label-mono">{t("email")}</Label>
            <Input data-testid="login-email" type="email" required value={email}
              onChange={e => setEmail(e.target.value)} className="rounded-none border-slate-300 focus-visible:ring-[#002FA7]" />
          </div>
          <div>
            <Label className="label-mono">{t("password")}</Label>
            <Input data-testid="login-password" type="password" required value={password}
              onChange={e => setPassword(e.target.value)} className="rounded-none border-slate-300 focus-visible:ring-[#002FA7]" />
          </div>
          <Button data-testid="login-submit" type="submit" disabled={loading}
            className="w-full rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono py-6">
            {loading ? t("starting") : t("submit")}
          </Button>
        </form>
        <div className="flex items-center gap-4 my-6"><div className="flex-1 h-px bg-slate-200" /><span className="label-mono text-slate-400">{t("or_google")}</span><div className="flex-1 h-px bg-slate-200" /></div>
        <Button data-testid="login-google" onClick={googleLogin} variant="outline"
          className="w-full rounded-none border-slate-900 label-mono py-6 hover:bg-slate-900 hover:text-white">
          {t("google_btn")}
        </Button>
        <p className="text-sm text-slate-600 mt-6">{t("need_account")} <Link to="/register" className="underline text-[#002FA7]">{t("nav_register")}</Link></p>
      </div>
    </Layout>
  );
}
